import cx from 'classnames';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { WarningOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState, useRef, useCallback } from 'react';

import { Percentile } from '../interfaces';
import PreviousNextButtons from '../PreviousNextButtons';
import { Store } from '../../../../state/store.interfaces';
import Icon from '../../../../common/components/Icon/Icon';
import { ENVIRONMENT } from '../../../../config/config.api';
import IconButton from '../../../../common/components/buttons/IconButton';
import { LrvPopover } from '../../../../common/components/LrvPopover/LrvPopover';
import { checkAnalysisBelongToStocking, getAreaPercentIncrease } from '../../helpers';
import { groupLabels, createListPercentiles, createListPercentilesOld } from '../math_percentile';
import { environments, roundWeight, analysisTypes, bucketOriginalJuvenile, roles, getMainRole } from '../../../../config/commons';

import './Sample.scss';
import styles from './Sample.module.scss';
import * as sampleSlice from './sampleSlice';
import SampleD3, { SampleD3Props } from './SampleD3';
import { SampleFullScreen } from './SampleFullScreen';

let sampleChart: SampleD3 | null;

interface Props {
  showOptions?: boolean;
  containerClassName?: string;
  theme?: 'dark' | 'light';
}

export default function Sample (props: Props) {
  const { containerClassName = styles.container, showOptions = true, theme = 'dark' } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const [image, setImage] = useState(new window.Image());
  const [loading, setLoading] = useState(true);

  const { allAnalysis, analysis: analysisOriginal, currentIndex } = useSelector((state: Store) => state.detailAnalysis);
  const {
    editSample,
    newAnimal,
    showAnimalsRemoved,
    animalSelected,
    percentileList,
    isDownloadingSample,
    isDownloadingMask,
    isDownloadingJuvenileMask,
  } = useSelector((state: Store) => state.sample);

  const currentAnalysis = (allAnalysis && allAnalysis.length > 0) ? allAnalysis[currentIndex] : analysisOriginal;
  const analysisType = currentAnalysis?.type || '';
  const analysisPhaseType = currentAnalysis.phaseType;
  const larvaeModifiedList = currentAnalysis.inferenceData.larvaeModifiedList;
  const analysisBelongToStocking = checkAnalysisBelongToStocking({ analysisSelected: currentAnalysis });

  const refChart = useRef<HTMLDivElement>(null);

  const accessToken = localStorage.getItem('accessToken');

  const averageWeight = currentAnalysis.resultData.averageWeight;
  const imagenUrl = currentAnalysis.imageData.original?.url;
  const maskUrl = currentAnalysis.imageData.mask?.url;

  const updatePercentiles = useCallback(() => {
    if (!currentAnalysis._id || image.height === 0) {
      return;
    }

    const { pointSizeRatio } = currentAnalysis;
    const { larvaeNumber } = currentAnalysis.resultData;
    const { weightGroups } = currentAnalysis.inferenceData;

    const labels = roundWeight({ value: currentAnalysis.resultData.labels, showUnit: false, average: averageWeight });
    const labelsSort: string[] = groupLabels(labels as number[]);

    const areaPercentIncrease = getAreaPercentIncrease({ analysisType }) * larvaeNumber;
    const percentiles: Percentile[] = pointSizeRatio === undefined || pointSizeRatio === null ?
      createListPercentilesOld({ labelsSort, areaPercentIncrease, weightGroups }) :
      createListPercentiles({ labelsSort, pointSizeRatio, weightGroups, imageSize: image.height });

    dispatch(sampleSlice.setPercentileList(percentiles));
  }, [currentAnalysis._id, averageWeight, analysisType, image.height]);

  useEffect(() => {
    dispatch(sampleSlice.setChangesSaved(false));

    return (() => {
      sampleChart = null;
    });
  }, [currentIndex]);

  useEffect(() => {
    setLoading(true);

    if (!currentAnalysis?.imageData?.reduced?.url) {
      return;
    }

    const img = new window.Image();
    img.src = currentAnalysis.imageData.reduced.url;
    img.addEventListener('load', () => {
      setImage(img);
      setLoading(false);
    });
  }, [currentAnalysis?.imageData?.reduced?.url]);

  useEffect(() => {
    updatePercentiles();
  }, [updatePercentiles]);

  useEffect(() => {
    if (!currentAnalysis?.imageData?.reduced?.url || !percentileList?.length || !refChart.current || image.height === 0) {
      return;
    }

    if (sampleChart) {
      sampleChart.refreshChart({
        animalsByGroup: percentileList,
        showAnimalsRemoved,
        allowEditSample: false,
        newAnimal: undefined,
        animalSelected: undefined,
        currentIndex,
        urlImage: currentAnalysis.imageData.reduced.url,
      });

      return;
    }

    const props: SampleD3Props = {
      container: refChart.current,
      height: image.height,
      width: image.width,
      urlImage: currentAnalysis.imageData.reduced.url,
      animalsByGroup: percentileList,
      phaseType: analysisPhaseType,
      showAnimalsRemoved,
      translateX: - (image.width / 3),
      translateY: - (image.height / 3),
      enableScroll: false,
      allowEditSample: false,
    };
    sampleChart = new SampleD3(props);
  }, [refChart.current, currentIndex, percentileList, image.height, image.width, currentAnalysis._id, currentAnalysis?.imageData?.reduced?.url, analysisPhaseType, showAnimalsRemoved, larvaeModifiedList?.length, editSample, newAnimal, animalSelected, image]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.center}>
          <Spin />
        </div>
        <div className={styles.containerImage} id='sample'>
          <div ref={refChart} />
          <PreviousNextButtons />
        </div>
      </div>
    );
  }

  function renderButtonDownloadImage () {
    const higherRole = getMainRole();
    if (roles.SUPER_ADMIN !== higherRole) {
      return null;
    }

    const nameFile = currentAnalysis._id;
    return (
      <IconButton
        id='download_sample_button'
        loading={isDownloadingSample}
        onClick={() => dispatch(sampleSlice.downloadImage(imagenUrl, nameFile))}
        iconName='download'
        size='small'
        tooltipText={t('analysis.download.image')}
      />
    );
  }

  function renderWarnings () {
    const { showGrowOutWarning, unusualAverageWeight } = currentAnalysis;
    const showPopover = showGrowOutWarning || unusualAverageWeight;

    if (!showPopover) {
      return null;
    }

    const content = (
      <div style={{ marginLeft: -10 }}>
        <ul>
          {showGrowOutWarning && <li>{t('analysis.warnings.growOut')}</li>}
          {unusualAverageWeight && !analysisBelongToStocking && <li>{t('analysis.warnings.weightOutOfRange')}.</li>}
          {unusualAverageWeight && analysisBelongToStocking && <li>{t('analysis.warnings.weightOutOfRange')}, {t('analysis.warnings.belongsToAnotherStocking')}</li>}
        </ul>
      </div>
    );

    return (
      <LrvPopover content={content} title={t('analysis.warnings.title')}>
        <WarningOutlined className={styles.warningIcon} />
      </LrvPopover>
    );
  }

  function renderButtonDownloadMask () {
    if (environments.DEV !== ENVIRONMENT || !maskUrl || !accessToken) {
      return null;
    }

    const nameFile = currentAnalysis._id;
    const extension = analysisType === analysisTypes.GENERAL_LARVAE_ANALYSIS ? 'png' : 'json';
    const title = analysisType === analysisTypes.GENERAL_LARVAE_ANALYSIS ? t('analysis.download.mask') : t('analysis.download.jsonMask');

    return (
      <IconButton
        loading={isDownloadingMask}
        onClick={() => dispatch(sampleSlice.downloadMask(maskUrl, nameFile, extension))}
        icon={<Icon className='superAdminAction' name='download' theme={theme} />}
        size='small'
        tooltipText={title}
      />
    );
  }

  function renderButtonDownloadJuvenileImageMask () {
    if (analysisType === analysisTypes.GENERAL_JUVENILE_ANALYSIS && environments.DEV === ENVIRONMENT && maskUrl && accessToken) {
      const extension = 'jpg';
      const nameFile = currentAnalysis._id;

      return (
        <IconButton
          loading={isDownloadingJuvenileMask}
          onClick={() => dispatch(sampleSlice.generateJuvenileMask({ analysisCode: nameFile, imageName: currentAnalysis?.imageData?.original?.key, bucketName: bucketOriginalJuvenile, format: extension }))}
          icon={<Icon className={styles.generateJuvenileMask} name='download' theme={theme} />}
          size='small'
          tooltipText={t('analysis.download.mask')}
        />
      );
    }

    return null;
  }

  function renderButtonFullScreen () {
    return (
      <IconButton
        id='fullscreen_button'
        onClick={() => dispatch(sampleSlice.setShowModal(true))}
        iconName='fullscreen'
        size='small'
        tooltipText={t('analysis.fullscreen')}
      />
    );
  }

  function renderOption () {
    if (!showOptions) {
      return null;
    }

    return (
      <div className={styles.actions}>
        {renderWarnings()}
        {renderButtonDownloadImage()}
        {renderButtonDownloadMask()}
        {renderButtonDownloadJuvenileImageMask()}
        {renderButtonFullScreen()}
      </div>
    );
  }

  return (
    <div className={cx(containerClassName, 'sample')}>
      {renderOption()}

      <div className={styles.containerImage} id='sample'>
        <div ref={refChart} />
        <PreviousNextButtons />
      </div>

      <SampleFullScreen theme={theme} image={image} allowEditSample={!analysisOriginal.transferId}/>
    </div>
  );
}
